import { useAttribution } from "../../hooks/useAttribution";
import type { AttributionCounts } from "../../hooks/useAttribution";

interface Props {
  brandId: string;
  days?: number;
}

function formatCurrency(currency: string, cents: number): string {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}
const formatRevenue = (revenue: Record<string, number>) => {
  const entries = Object.entries(revenue).filter(([, cents]) => cents !== 0);
  if (entries.length === 0) return formatCurrency("USD", 0);
  return entries.map(([currency, cents]) => formatCurrency(currency, cents)).join(" + ");
};

function Totals({ counts }: { counts: AttributionCounts }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {[
        { label: "Signups", value: String(counts.signups) },
        { label: "Sales", value: String(counts.sales) },
        { label: "Revenue", value: formatRevenue(counts.revenue_by_currency) },
      ].map((tile) => (
        <div key={tile.label} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-3 text-center">
          <p className="text-2xl font-semibold text-slate-900">{tile.value}</p>
          <p className="mt-0.5 text-xs text-slate-500">{tile.label}</p>
        </div>
      ))}
    </div>
  );
}

function CountCells({ counts }: { counts: AttributionCounts }) {
  return (
    <>
      <td className="px-3 py-1.5 text-right tabular-nums">{counts.signups}</td>
      <td className="px-3 py-1.5 text-right tabular-nums">{counts.sales}</td>
      <td className="px-3 py-1.5 text-right tabular-nums">{formatRevenue(counts.revenue_by_currency)}</td>
    </>
  );
}

function CountHeaders({ first }: { first: string }) {
  return (
    <thead>
      <tr className="text-xs text-slate-500">
        <th className="px-3 py-1.5 text-left font-medium">{first}</th>
        <th className="px-3 py-1.5 text-right font-medium">Signups</th>
        <th className="px-3 py-1.5 text-right font-medium">Sales</th>
        <th className="px-3 py-1.5 text-right font-medium">Revenue</th>
      </tr>
    </thead>
  );
}

export function AttributionWidget({ brandId, days = 30 }: Props) {
  const { data: response, isLoading, error } = useAttribution(brandId, days);

  if (isLoading) {
    return (
      <div className="space-y-2 animate-pulse" aria-busy="true">
        <div className="h-4 w-32 rounded bg-slate-200" />
        <div className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-lg bg-slate-100" />)}
        </div>
      </div>
    );
  }

  if (error || !response) {
    return <p className="text-sm text-slate-500">Attribution unavailable</p>;
  }

  if (response.status !== "ok") {
    return (
      <div className="space-y-1 text-sm text-slate-600">
        <p className="font-medium text-slate-800">Attribution is not set up for this brand.</p>
        <p>
          Connect a signed conversion webhook and choose which events count as signups and sales.
          Posts already carry brand-tagged links, so conversions will join back to them.
        </p>
      </div>
    );
  }

  const report = response.data;
  const hasConversions = report.totals.signups + report.totals.sales > 0;
  const hasUnattributed = report.unattributed.signups + report.unattributed.sales > 0;

  return (
    <div className="space-y-4">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Last {report.window.days} days</p>
      <Totals counts={report.totals} />

      {!hasConversions ? (
        <p className="text-sm text-slate-500">No conversions reported in this window yet.</p>
      ) : null}

      {report.by_channel.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-slate-100">
          <table className="w-full text-sm text-slate-700">
            <CountHeaders first="Source / medium" />
            <tbody className="divide-y divide-slate-100">
              {report.by_channel.map((row) => (
                <tr key={`${row.source}/${row.medium}`}>
                  <td className="px-3 py-1.5">{row.source} / {row.medium}</td>
                  <CountCells counts={row} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {report.by_post.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-slate-100">
          <table className="w-full text-sm text-slate-700">
            <CountHeaders first="Top posts" />
            <tbody className="divide-y divide-slate-100">
              {report.by_post.slice(0, 10).map((post) => (
                <tr key={post.brief_id}>
                  <td className="px-3 py-1.5">
                    {post.published_url ? (
                      <a href={post.published_url} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline">
                        {post.title ?? post.brief_id}
                      </a>
                    ) : (
                      <span>{post.title ?? post.brief_id}</span>
                    )}
                    {post.surface ? <span className="ml-2 text-xs text-slate-400">{post.surface}</span> : null}
                  </td>
                  <CountCells counts={post} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {hasUnattributed ? (
        <p className="text-xs text-slate-500">
          Unattributed: {report.unattributed.signups} signups, {report.unattributed.sales} sales
          ({formatRevenue(report.unattributed.revenue_by_currency)}) arrived without a HelloConvo campaign link.
        </p>
      ) : null}
      {report.truncated ? (
        <p className="text-xs text-amber-600">Showing the most recent 20,000 conversions; older ones in this window are not counted.</p>
      ) : null}
    </div>
  );
}
